import React from 'react';
import { Lock, UserCheck } from 'lucide-react';
import { useAccount } from '../../../../hooks/useAccount';
import { useEventDetails } from '../../../../hooks/useEventDetails';
import { useEventContext } from '../../../contexts/EventContext';

// V1 section components
import { V1DetailsSection } from './V1DetailsSection';
import { V1PhotosSection } from './V1PhotosSection';
import { V1FilesSection } from './V1FilesSection';
import { V1SponsorListSection } from './V1SponsorListSection';
import { V1BulletinBoardSection } from './V1BulletinBoardSection';
import { V1LocationSection } from './V1LocationSection';
import { V1CompanyListSection } from './V1CompanyListSection';
import { V1AttendeeListSection } from './V1AttendeeListSection';
import { V1FormsSection } from './V1FormsSection';

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  details?: string;
  position: number;
  sectionOption?: {
    accountRequired?: boolean;
    registrationRequired?: boolean;
    [key: string]: any;
  };
  attachments?: any[];
  sectionData?: any;
}

interface V1SectionRendererProps {
  section: Section;
}

/**
 * V1 Section Renderer - FROZEN
 *
 * Routes sections to appropriate V1 components based on type.
 * Supports all V1 section types including those not in V2.
 *
 * DO NOT add V2 features to this component.
 */
export const V1SectionRenderer: React.FC<V1SectionRendererProps> = ({ section }) => {
  const { eventSlug } = useEventContext();
  const { data: account } = useAccount();
  const { data: eventDetails } = useEventDetails(eventSlug);

  console.log('V1SectionRenderer - section type:', section.typeOf, 'section:', section);

  const isRegistered = eventDetails?.currentUserRegistered || false;

  // Check section-level access control
  const sectionOption = section.sectionOption || section.sectionOption;

  // BaseApi converts snake_case to camelCase
  if (sectionOption?.accountRequired && !account) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <Lock className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <h3 className="text-gray-800 text-lg font-semibold mb-2">
          Sign In Required
        </h3>
        <p className="text-gray-600">
          You must be signed in to view this section.
        </p>
      </div>
    );
  }

  if (sectionOption?.registrationRequired && !isRegistered) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <UserCheck className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <h3 className="text-gray-800 text-lg font-semibold mb-2">
          Registration Required
        </h3>
        <p className="text-gray-600">
          You must be registered for this event to view this section.
        </p>
      </div>
    );
  }

  // Route to appropriate section component based on type
  // BaseApi converts snake_case to camelCase
  const sectionType = section.typeOf || section.typeOf;
  switch (sectionType) {
    case 'details':
    case 'text':
      return <V1DetailsSection section={section} />;

    case 'photos':
      return <V1PhotosSection section={section} />;

    case 'files':
      return <V1FilesSection section={section} />;

    case 'sponsor_list':
      return <V1SponsorListSection section={section} />;

    case 'bulletin_board':
      return <V1BulletinBoardSection section={section} />;

    case 'location':
      return <V1LocationSection section={section} />;

    case 'company_list':
      return <V1CompanyListSection section={section} />;

    case 'attendee_list':
      // TODO: Decide how to handle attendee list for V1 events.
      // For now, we're not rendering this section.
      return null;

    case 'forms':
      return <V1FormsSection section={section} />;

    default:
      console.warn(`Unknown V1 section type: ${sectionType}`);
      return null;
  }
};